import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseCSV } from '../utils/csv';

const DEFAULT_DESCRIPTION = '';
const DEFAULT_THUMBNAIL = '/sriseetharamaswamy1.jpg';

const Icons = {
  Youtube: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.42a2.78 2.78 0 0 0-1.94 2C1 8.11 1 12 1 12s0 3.89.46 5.58a2.78 2.78 0 0 0 1.94 2c1.72.42 8.6.42 8.6.42s6.88 0 8.6-.42a2.78 2.78 0 0 0 1.94-2C23 15.89 23 12 23 12s0-3.89-.46-5.58z"></path><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"></polygon></svg>
  ),
  Audio: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
  ),
  Video: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect><line x1="7" y1="2" x2="7" y2="22"></line><line x1="17" y1="2" x2="17" y2="22"></line><line x1="2" y1="12" x2="22" y2="12"></line><line x1="2" y1="7" x2="7" y2="7"></line><line x1="2" y1="17" x2="7" y2="17"></line><line x1="17" y1="17" x2="22" y2="17"></line><line x1="17" y1="7" x2="22" y2="7"></line></svg>
  )
};

function getYoutubeVideoId(url) {
  if (!url) return '';
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.hostname.includes('youtu.be')) return parsedUrl.pathname.replace('/', '');
    if (parsedUrl.hostname.includes('youtube.com')) return parsedUrl.searchParams.get('v') || parsedUrl.pathname.split('/').filter(Boolean).pop() || '';
  } catch (error) {
    return '';
  }
  return '';
}

function normalizePublicSource(source) {
  if (!source) return '';
  if (source.startsWith('http://') || source.startsWith('https://') || source.startsWith('/')) return source;
  return `/${source}`;
}

function getMediaType(rawType, source) {
  const normalizedType = String(rawType || '').trim().toLowerCase();
  if (['audio', 'video', 'youtube'].includes(normalizedType)) return normalizedType;
  const normalizedSource = String(source || '').toLowerCase();
  if (normalizedSource.includes('youtube.com') || normalizedSource.includes('youtu.be')) return 'youtube';
  if (/\.(mp3|wav|ogg|m4a|aac)$/i.test(normalizedSource)) return 'audio';
  return 'video';
}

function getThumbnail(item) {
  if (item.thumbnail) return item.type === 'youtube' ? item.thumbnail : normalizePublicSource(item.thumbnail);
  if (item.type === 'youtube' && item.youtubeId) return `https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`;
  return DEFAULT_THUMBNAIL;
}

function buildMediaItem(row, index) {
  const type = getMediaType(row.type, row.source);
  const youtubeId = type === 'youtube' ? getYoutubeVideoId(row.source) : '';
  return {
    id: row.id || `media-${index + 1}`,
    title: row.title || `దైవీయ మాధ్యమం ${index + 1}`,
    title_en: row.title_en || '',
    category: row.category || 'మరికొన్ని',
    type,
    source: type === 'youtube' ? row.source : normalizePublicSource(row.source),
    youtubeId,
    thumbnail: row.thumbnail || '',
    poster: row.poster || row.thumbnail || '',
    description: row.description || DEFAULT_DESCRIPTION
  };
}

const Media = ({ mediaItems, setMediaItems, selectedMediaId, setSelectedMediaId, isVisible }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('అన్నీ');
  const nativePlayerRef = useRef(null);

  const categories = ['అన్నీ', 'రామదాసు కీర్తనలు', 'త్యాగరాజ కీర్తనలు', 'అన్నమయ్య కీర్తనలు', 'రామాయణం - చాగంటి', 'మల్లాది వారి రామాయణం', 'ప్రవచనాలు', 'హనుమాన్ చాలీసా', 'మరికొన్ని'];

  useEffect(() => {
    if (mediaItems.length > 0) return;
    
    fetch('/media.csv')
      .then((res) => res.text())
      .then((text) => {
        const parsed = parseCSV(text).map(buildMediaItem).filter((item) => item.source && item.title);
        setMediaItems(parsed);
        if (parsed.length && !selectedMediaId) {
          const defaultId = 'ramadasu-5rj2j';
          const hasDefault = parsed.some(item => item.id === defaultId);
          setSelectedMediaId(hasDefault ? defaultId : parsed[0].id);
        }
      })
      .catch((err) => console.error('Failed to load media.csv', err));
  }, [mediaItems, setMediaItems, selectedMediaId, setSelectedMediaId]);

  const filteredItems = useMemo(() => {
    return mediaItems.filter(item => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = item.title.toLowerCase().includes(search) || 
                            (item.title_en && item.title_en.toLowerCase().includes(search));
      const matchesCategory = activeCategory === 'అన్నీ' || item.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [mediaItems, searchTerm, activeCategory]);

  const groupedItems = useMemo(() => {
    const groups = filteredItems.reduce((acc, item) => {
      const cat = item.category || 'మరికొన్ని';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});

    const order = activeCategory === 'అన్నీ' 
      ? ['రామదాసు కీర్తనలు', 'త్యాగరాజ కీర్తనలు', 'అన్నమయ్య కీర్తనలు', 'రామాయణం - చాగంటి', 'మల్లాది వారి రామాయణం', 'ప్రవచనాలు', 'హనుమాన్ చాలీసా', 'మరికొన్ని']
      : [activeCategory];

    return order.map(cat => ({
      name: cat,
      items: groups[cat] || []
    })).filter(g => g.items.length > 0);
  }, [filteredItems, activeCategory]);

  const selectedMedia = useMemo(
    () => mediaItems.find((item) => item.id === selectedMediaId) || mediaItems[0] || null,
    [mediaItems, selectedMediaId]
  );

  return (
    <section className={`media-page page-content`} style={{ display: isVisible ? 'block' : 'none' }}>
      {selectedMedia ? (
        <div className="media-main-layout">
          <div className="media-player-container">
            <div className="player-wrapper">
              {selectedMedia.type === 'youtube' ? (
                <div className="video-aspect-ratio">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedMedia.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                    title={selectedMedia.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              ) : selectedMedia.type === 'audio' ? (
                <div className="audio-player-wrapper">
                  <img src={getThumbnail(selectedMedia)} alt={selectedMedia.title} className="audio-cover-art" />
                  <audio ref={nativePlayerRef} className="custom-audio-player" controls autoPlay>
                    <source src={selectedMedia.source} />
                  </audio>
                </div>
              ) : (
                <video ref={nativePlayerRef} className="video-element" controls autoPlay playsInline poster={selectedMedia.poster ? normalizePublicSource(selectedMedia.poster) : getThumbnail(selectedMedia)}>
                  <source src={selectedMedia.source} />
                </video>
              )}
            </div>
          </div>

          <div className="media-list-container">
            <div className="media-list-controls">
              <div className="media-nav-menu">
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`media-nav-item ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              
              <div className="media-search-wrapper">
                <input
                  type="text"
                  placeholder="వెతకండి..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="media-search-input"
                />
              </div>
            </div>

            <div className="media-scroll-list">
              {groupedItems.map((group) => (
                <div key={group.name} className="media-section">
                  <h3 className="media-section-title">{group.name}</h3>
                  <div className="media-section-items">
                    {group.items.map((item) => (
                      <div key={item.id} className={`media-list-card ${selectedMediaId === item.id ? 'active' : ''}`} onClick={() => setSelectedMediaId(item.id)}>
                        <div className="card-thumb">
                          <img src={getThumbnail(item)} alt={item.title} />
                          <div className="thumb-icon">
                            {item.type === 'youtube' && <Icons.Youtube />}
                            {item.type === 'audio' && <Icons.Audio />}
                            {item.type === 'video' && <Icons.Video />}
                          </div>
                        </div>
                        <div className="card-content">
                          <h4 className="card-title">{item.title}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {filteredItems.length === 0 && (
                <div className="empty-search-state">ఏమీ కనుగొనబడలేదు.</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="media-empty-state">
          <h3>క్షమించండి, కీర్తనలు అందుబాటులో లేవు.</h3>
        </div>
      )}
    </section>
  );
};

export default Media;
